
import MetadataJson from 'libphonenumber-js/metadata.mobile.json';
import { CountryCode, parsePhoneNumberFromString, PhoneNumber } from 'libphonenumber-js/mobile';
import { merge, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Self,
  Input,
  OnInit,
  Optional,
  Component,
  OnDestroy,
  ViewChild,
  OnChanges,
  ElementRef,
  HostBinding,
  SimpleChanges,
} from '@angular/core';
import {
  NgForm,
  UntypedFormGroup,
  NgControl,
  UntypedFormBuilder,
  AbstractControl,
  FormGroupDirective,
  ControlValueAccessor,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';

import { mouseOutside } from '@lib/common/helpers';
import { LocaleService } from '@lib/translation';

class MobilePhoneControlBase {
  constructor(
    public _parentForm: NgForm,
    public _parentFormGroup: FormGroupDirective,
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    public ngControl: NgControl,
  ) {}
}

@Component({
  selector: 'sn-mobile-phone-control',
  templateUrl: './mobile-phone.control.html',
  styleUrls: ['./mobile-phone.control.scss'],
  inputs: ['disabled'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: SnMobilePhoneControl,
    },
  ],
})
export class SnMobilePhoneControl
  extends MobilePhoneControlBase
  implements OnChanges, OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<string>
{
  public static nextId = 0;

  public stateChanges: Subject<void> = new Subject();

  public disabled: boolean;

  public errorState: boolean;

  @Input()
  get value(): string {
    const currentValue = this.numberControl.value;
    
    if (!currentValue) {
      return null;
    }

    const phoneNumber = this._parsePhoneNumber(currentValue);
    return phoneNumber ? phoneNumber.formatInternational() : null;
  }
  set value(value: string) {
    let phoneNumber;
    if (value) {
      phoneNumber = parsePhoneNumberFromString(value, this.code);
    }
    if (phoneNumber) {
      this.codeControl.setValue(phoneNumber.countryCallingCode);
      // Use formatNational() to preserve leading zeros (e.g., French 06..., UK 07...)
      const nationalFormat = phoneNumber.formatNational();
      this.numberControl.setValue(nationalFormat, { emitEvent: false });
    } else {
      this.numberControl.setValue(value, { emitEvent: false });
    }
    this.stateChanges.next();
  }

  get region(): string {
    const countryCode = this._localeService.getPhoneDefaultCountryCode();
    const callingCode = this._getCallingCodeForCountry(countryCode);
    return callingCode;
  }
  
  get code(): CountryCode {
    return this._localeService.getPhoneDefaultCountryCode();
  }

  @Input()
  get placeholder(): string {
    return this._placeholder && this._placeholder.trim() !== '' 
      ? this._placeholder 
      : this._localeService.getPhonePlaceholder();
  }
  set placeholder(value: string) {
    this._placeholder = value && value.trim() !== '' ? value : undefined;
    this.stateChanges.next();
  }

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(req: boolean) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  @HostBinding()
  public id = `sn-mobile-phone-control-${SnMobilePhoneControl.nextId++}`;

  @HostBinding('attr.aria-describedby')
  public describedBy = '';

  @HostBinding('class.floating')
  public shouldLabelFloat = true;

  @ViewChild('select', { read: ElementRef })
  public selectRef: ElementRef;

  @ViewChild('input', { read: ElementRef })
  public inputRef: ElementRef;

  public focused = false;
  public controlType = 'sn-mobile-phone-control';
  public selected = this.region;
  public form: UntypedFormGroup;

  public readonly codeList: string[] = Object.keys(MetadataJson.country_calling_codes);

  private _placeholder: string;
  private _required = false;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Optional() parentForm: NgForm,
    @Optional() parentFormGroup: FormGroupDirective,
    @Optional() @Self() public readonly ngControl: NgControl,
    defaultErrorStateMatcher: ErrorStateMatcher,
    private readonly _focusMonitor: FocusMonitor,
    private readonly _elementRef: ElementRef<HTMLElement>,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _localeService: LocaleService,
  ) {
    super(parentForm, parentFormGroup, defaultErrorStateMatcher, ngControl);

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this.form = this._formBuilder.group({
      code: this.region,
      number: '',
    });

    this._focusMonitor
      .monitor(this._elementRef.nativeElement, true)
      .pipe(takeUntil(this._destroy$))
      .subscribe((origin) => {
        this.focused = !!origin;
        this.stateChanges.next();
      });
  }

  get empty(): boolean {
    return !this.numberControl.value;
  }

  get codeControl(): AbstractControl {
    return this.form.get('code');
  }

  get numberControl(): AbstractControl {
    return this.form.get('number');
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled) {
      this.stateChanges.next();
    }
  }

  public ngOnInit(): void {
    this.stateChanges.next();

    this.codeControl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
      setTimeout(this.focus.bind(this));
    });

    merge(this.codeControl.valueChanges, this.numberControl.valueChanges)
      .pipe(takeUntil(this._destroy$))
      .subscribe(this._onUpdate.bind(this));

    this.form.statusChanges
      .pipe(
        filter(() => this.numberControl.touched),
        filter((status: string) => status === 'INVALID'),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this.ngControl.control.setErrors({
          invalidMobilePhone: true,
          ...this.ngControl.control.errors,
        });
      });

    this._localeService.phoneCountryCode$
      .pipe(takeUntil(this._destroy$))
      .subscribe((countryCode) => {
        const callingCode = this._getCallingCodeForCountry(countryCode);
        if (this.codeControl.value !== callingCode && this.empty) {
          this.codeControl.setValue(callingCode, { emitEvent: false });
        }
      });
      
    this._localeService.phonePlaceholder$
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.stateChanges.next();
      });
  }

  public ngOnDestroy(): void {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef.nativeElement);

    this._destroy$.next();
    this._destroy$.complete();
  }

  public trackCode(index: number, item: string): any {
    return index;
  }

  public focus(): void {
    this.inputRef.nativeElement.focus();
  }

  public onPaste(event: ClipboardEvent): void {
    this.value = event.clipboardData.getData('text/plain');
    event.preventDefault();
  }

  public onBlur(): void {
    this._onUpdate();
    this.ngControl.control.markAsTouched();
  }

  public setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  public onContainerClick(event: MouseEvent): void {
    if (mouseOutside(event, this.selectRef.nativeElement)) {
      this.focus();
    }
  }

  public writeValue(value: any): void {
    this.value = value;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;

    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }

    this.stateChanges.next();
  }

  public registerOnChange(fn: (value: any) => void): void {
    this._changeFn = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._touchedFn = fn;
  }

  private _changeFn: (value: any) => void = () => {};
  private _touchedFn: () => void = () => {};

  private _onUpdate(): void {
    const currentValue = this.numberControl.value;
    
    if (!currentValue) {
      this._changeFn(null);
      this.form.setErrors(null);
      return;
    }

    const startsWithZero = currentValue.trim().startsWith('0');
    const phoneNumber = this._parsePhoneNumber(currentValue);

    if (phoneNumber) {
      const formatedNumber = phoneNumber.formatNational();
  
      const finalNumber = startsWithZero && !formatedNumber.startsWith('0')
        ? '0' + formatedNumber
        : formatedNumber;

      this.numberControl.setValue(finalNumber, { emitEvent: false });
      this._changeFn(this.value);

      if (!phoneNumber.isPossible()) {
        this.form.setErrors({ invalid: true });
      } else {
        this.form.setErrors(null);
      }
    } else {
      this._changeFn(null);
      this.form.setErrors({ invalid: true });
    }
  }

  private _parsePhoneNumber(currentValue: string): PhoneNumber | undefined {
    const inputWithCountryCode = `+${this.codeControl.value}${currentValue}`;
    let phoneNumber = parsePhoneNumberFromString(inputWithCountryCode);

    if (!phoneNumber && currentValue.startsWith('0')) {
      phoneNumber = parsePhoneNumberFromString(currentValue, this.code);
    }

    return phoneNumber;
  }

  private _getCallingCodeForCountry(countryCode: CountryCode): string {
    const metadata = MetadataJson.countries[countryCode];
    if (metadata && metadata[0]) {
      return metadata[0].toString();
    }
    return '1';
  }
}
